import { useQueryClient } from "react-query";
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { Container, Nav, Navbar } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance, { baseURL } from "@helper/interceptor";
import { useBackgroundCustomerBasicInfo } from "@hooks/useBackgroundData";
import DualSidebarNav from "@components/Navigation/DualSidebarNav";
import KeyboardShortcutsModal from "@components/Navigation/KeyboardShortcutsModal";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "@hooks/useWindowDimension";

function AlertDismissibleCustomer() {
  const [show, setShow] = useState(true);
  const { t } = useTranslation();

  if (show) {
    return (
      <Alert className="alert-wrapper" variant="danger" onClose={() => setShow(false)} dismissible>
        <Alert.Heading>{t("demo_project_warning.title")}</Alert.Heading>
        <p>{t("demo_project_warning.description")}</p>
      </Alert>
    );
  }
  return <Button onClick={() => setShow(true)}>Show Alert</Button>;
}

function Header() {
  const [customerImage, setCustomerImage] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [showShortcuts, setShowShortcuts] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { mobile } = useSelector((state) => state.mobile);
  const { width } = useWindowDimensions();
  const [showTreeSenseLogo, setShowTreeSenseLogo] = useState(true);
  const leftContentRef = useRef(null);
  const rightContentRef = useRef(null);
  const customerLogoRef = useRef(null);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);
  const TREE_LOGO_WIDTH = 96; // logo width + small gap
  const CENTER_SIDE_GUTTER = 8;
  const CENTER_FIT_TOLERANCE = 48;

  // Use background data hook
  const {
    customerBasicInfo,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useBackgroundCustomerBasicInfo();

  // Update local state when background data changes
  useEffect(() => {
    if (customerBasicInfo) {
      setCustomerImage(customerBasicInfo.image || "");
      setCustomerName(customerBasicInfo.name || "");
    }
  }, [customerBasicInfo, isCustomerLoading, isCustomerError]);

  const logoutInProgress = useRef(false);

  const handleLogout = () => {
    // Prevent multiple logout attempts
    if (logoutInProgress.current) return;
    logoutInProgress.current = true;

    // Completely bypass React/Redux - direct browser-level logout
    const performImmediateLogout = async () => {
      try {
        // Cancel all queries first
        queryClient.cancelQueries();
        queryClient.clear();

        // Clear all storage immediately
        localStorage.clear();
        sessionStorage.clear();

        // Clear cookies
        document.cookie.split(";").forEach((c) => {
          const eqPos = c.indexOf("=");
          const name = eqPos > -1 ? c.substr(0, eqPos) : c;
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          document.cookie =
            name +
            "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" +
            window.location.hostname;
        });

        // Backend logout (non-blocking)
        axiosInstance
          .get(`${baseURL}logout/`, { priority: "high", timeout: 2000 })
          .catch((err) => console.log("Backend logout failed:", err));

        // Force immediate navigation
        window.location.href = "/login";
      } catch (error) {
        console.error("Logout error:", error);
        // Still navigate even if cleanup fails
        window.location.href = "/login";
      }
    };

    // Execute immediately without any delays
    performImmediateLogout();
  };

  const getCurrentPageTitle = () => {
    const path = location.pathname || "";
    if (path.startsWith("/dashboard")) return t("dashboard.title");
    if (path.startsWith("/trees")) return t("tree_cadastre");
    if (path.startsWith("/map")) return t("map_view");
    if (path.startsWith("/sensors")) return t("sensors");
    if (path.startsWith("/projects")) return t("projects");
    if (path.startsWith("/project/")) return t("projects");
    if (path.startsWith("/tasks")) return t("tasks");
    if (path.startsWith("/areas")) return t("areas");
    if (path.startsWith("/data-science")) return t("data_science");
    if (path.startsWith("/settings")) return t("settings");
    if (path.startsWith("/feedback")) return t("feedback");
    if (path.startsWith("/reporting")) return t("reporting");
    return "";
  };

  const currentPageTitle = getCurrentPageTitle();

  useLayoutEffect(() => {
    const recalc = () => {
      const leftWidth = leftContentRef.current?.offsetWidth || 0;
      const rightWidth = rightContentRef.current?.offsetWidth || 0;
      const customerWidth = customerLogoRef.current?.offsetWidth || 0;
      const availableCenterWidth = Math.max(0, width - leftWidth - rightWidth - CENTER_SIDE_GUTTER);
      const requiredCenterWidth = customerWidth + TREE_LOGO_WIDTH;
      setShowTreeSenseLogo(requiredCenterWidth <= availableCenterWidth + CENTER_FIT_TOLERANCE);
    };

    const raf = requestAnimationFrame(recalc);
    window.addEventListener("resize", recalc);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", recalc);
    };
  }, [width, currentPageTitle, customerImage, mobile]);

  // Mobile auto-hide top bar on scroll
  const TOP_THRESHOLD = 16;
  const DELTA_THRESHOLD = 8;

  const handleScroll = useCallback(() => {
    const currentY = window.scrollY;
    const delta = currentY - lastScrollYRef.current;

    if (currentY < TOP_THRESHOLD) {
      setIsHeaderHidden(false);
      lastScrollYRef.current = currentY;
      tickingRef.current = false;
      return;
    }

    if (Math.abs(delta) < DELTA_THRESHOLD) {
      tickingRef.current = false;
      return;
    }

    if (delta > 0) {
      setIsHeaderHidden(true);
    } else {
      setIsHeaderHidden(false);
    }

    lastScrollYRef.current = currentY;
    tickingRef.current = false;
  }, []);

  useEffect(() => {
    if (!mobile) {
      setIsHeaderHidden(false);
      return;
    }

    const onScroll = () => {
      if (!tickingRef.current) {
        tickingRef.current = true;
        requestAnimationFrame(handleScroll);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mobile, handleScroll]);

  // Reset header visibility on route change
  useEffect(() => {
    setIsHeaderHidden(false);
    lastScrollYRef.current = window.scrollY;
  }, [location.pathname]);

  // Keyboard shortcuts (mirrors TreeSenseNavigation without changing layout)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isInputFocused = ["INPUT", "TEXTAREA", "SELECT"].includes(
        document.activeElement.tagName
      );
      if (isInputFocused) return;

      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + K: toggle main menu sidebar button (if present)
      if (cmdOrCtrl && e.key === "k") {
        e.preventDefault();
        const sidebarToggle = document.querySelector(".sidebar-toggle-btn");
        if (sidebarToggle) sidebarToggle.click();
        return;
      }

      // Cmd/Ctrl + S: focus search (if present elsewhere)
      if (cmdOrCtrl && e.key === "s") {
        e.preventDefault();
        const searchInput = document.querySelector(".search-input");
        if (searchInput) searchInput.focus();
        return;
      }

      // Cmd/Ctrl + B: toggle mobile menu (if present elsewhere)
      if (cmdOrCtrl && e.key === "b") {
        e.preventDefault();
        if (mobile) {
          const mobileMenuBtn = document.querySelector(".mobile-menu-btn");
          if (mobileMenuBtn) mobileMenuBtn.click();
        }
        return;
      }

      // Escape: close overlays (best-effort)
      if (e.key === "Escape") {
        const activeDropdown = document.querySelector(".dropdown-open");
        if (activeDropdown) activeDropdown.click();
        if (showShortcuts) setShowShortcuts(false);
        return;
      }

      // ?: show shortcuts modal
      if (e.key === "?") {
        e.preventDefault();
        setShowShortcuts(true);
        return;
      }

      // Number keys: quick navigation
      if (!cmdOrCtrl && !e.shiftKey && !e.altKey) {
        const orderedRoutes = [
          "/dashboard",
          "/trees",
          "/map",
          "/sensors",
          "/projects",
          "/tasks",
          "/data-science",
          "/settings",
        ];
        const index = Number(e.key) - 1;
        if (index >= 0 && index < orderedRoutes.length) {
          navigate(orderedRoutes[index]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobile, navigate]);

  return (
    <>
      <header className="header">
        <Navbar
          bg="primary"
          variant="dark"
          expand="lg"
          className={`fixed-top p-0 shadow-sm app-topbar${
            mobile && isHeaderHidden ? " app-topbar--hidden" : ""
          }`}
        >
          <Container fluid className="header__inner">
            <div className="header__left">
              <Nav className="header__start">
                {/* Dual Sidebar Navigation Toggles */}
                <div ref={leftContentRef}>
                  <DualSidebarNav onLogout={handleLogout} />
                </div>
              </Nav>
            </div>
            <div className="header__center">
              <div className="header__middle">
                {showTreeSenseLogo && (
                  <Navbar.Brand aria-label="logo" className="m-0">
                    <img
                      src={process.env.PUBLIC_URL + "/img/treesense-logo.webp"}
                      className="align-middle"
                      height="41"
                      width="90"
                      alt="Treesense Logo"
                    />
                  </Navbar.Brand>
                )}
                <div ref={customerLogoRef}>
                  <Navbar.Brand href="/" aria-label="logo-org" className="m-0">
                    <img
                      src={customerImage}
                      className="align-end mt-2"
                      height="35"
                      alt=""
                      style={{ display: customerImage ? "block" : "none" }}
                    />
                  </Navbar.Brand>
                </div>
              </div>
            </div>
            <div className="header__right">
              <div className="header__end" ref={rightContentRef}>
                {currentPageTitle && (
                  <span
                    className="text-white fw-semibold pe-3"
                    style={{ fontSize: mobile ? "0.85rem" : "0.95rem", whiteSpace: "nowrap" }}
                  >
                    {currentPageTitle}
                  </span>
                )}
              </div>
            </div>
          </Container>
        </Navbar>
      </header>
      {customerName == "Demo" && <AlertDismissibleCustomer />}
      <KeyboardShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </>
  );
}

export default React.memo(Header);
